import React, { useEffect, useState } from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import {
  Timer as TimerIcon,
  CheckCircle as MetIcon,
  ErrorOutline as BreachIcon,
} from '@mui/icons-material';

const TWO_HOURS = 2 * 60 * 60 * 1000;

export const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'object') {
    if (typeof value.toDate === 'function') return value.toDate();
    const secs = value._seconds ?? value.seconds;
    if (typeof secs === 'number') return new Date(secs * 1000);
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

/** "3d 4h", "2h 15m", "45m", "<1m" */
export const formatDuration = (ms) => {
  const abs = Math.abs(ms);
  const minutes = Math.floor(abs / 60000);
  if (minutes < 1) return '<1m';
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  if (days > 0) return hours ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return mins ? `${hours}h ${mins}m` : `${hours}h`;
  return `${mins}m`;
};

/** Minutes -> human duration ("4h", "1d 12h", "30m"). */
export const minutesToHuman = (minutes) => {
  const n = parseInt(minutes, 10);
  if (!n || n < 1) return '-';
  return formatDuration(n * 60000);
};

/**
 * Compute the display state for one SLA target.
 * Returns { color, label, icon, detail } where color is one of
 * success (>2h left), warning (<2h left), error (breached) or default (met).
 */
export const slaTargetState = ({ label, by, at, breached, now }) => {
  const deadline = toDate(by);
  const done = toDate(at);
  if (!deadline) return null;
  const nowMs = (now || new Date()).getTime();
  if (done) {
    const late = breached || done > deadline;
    return late
      ? { color: 'error', icon: <BreachIcon />, label: `${label} late (+${formatDuration(done - deadline)})`, detail: `Target ${deadline.toLocaleString()} · met ${done.toLocaleString()}` }
      : { color: 'default', icon: <MetIcon />, label: `${label} met`, detail: `Target ${deadline.toLocaleString()} · met ${done.toLocaleString()}` };
  }
  const remaining = deadline.getTime() - nowMs;
  if (remaining <= 0) {
    return { color: 'error', icon: <BreachIcon />, label: `${label} overdue ${formatDuration(remaining)}`, detail: `Target was ${deadline.toLocaleString()}` };
  }
  return {
    color: remaining < TWO_HOURS ? 'warning' : 'success',
    icon: <TimerIcon />,
    label: `${label} in ${formatDuration(remaining)}`,
    detail: `Target ${deadline.toLocaleString()}`,
  };
};

/** Ticking clock so countdowns refresh without a reload. */
export const useNow = (intervalMs = 60000) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const handle = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(handle);
  }, [intervalMs]);
  return now;
};

/**
 * Respond-by / resolve-by countdown chips for a case.
 * Props: sla (case.sla), status (case.status), compact (single-line, short labels), size.
 */
export const SlaBadge = ({ sla, status, compact = false, size = 'small' }) => {
  const now = useNow();
  if (!sla || sla.enabled === false) return compact ? <span>-</span> : null;

  const resolvedStatus = ['Resolved', 'Closed'].includes(status);
  const response = slaTargetState({ label: 'Respond', by: sla.respondBy, at: sla.respondedAt, breached: sla.responseBreached, now });
  const resolution = slaTargetState({
    label: 'Resolve',
    by: sla.resolveBy,
    at: sla.resolvedAt || (resolvedStatus ? sla.resolveBy : null),
    breached: sla.resolutionBreached,
    now,
  });

  const chips = [
    response && { key: 'response', ...response },
    resolution && { key: 'resolution', ...resolution },
  ].filter(Boolean);
  if (!chips.length) return compact ? <span>-</span> : null;

  return (
    <Box display="flex" gap={0.5} flexWrap={compact ? 'nowrap' : 'wrap'} alignItems="center">
      {chips.map((chip) => (
        <Tooltip key={chip.key} title={chip.detail} arrow>
          <Chip
            size={size}
            color={chip.color}
            variant={chip.color === 'default' ? 'outlined' : 'filled'}
            icon={chip.icon}
            label={chip.label}
            sx={{ maxWidth: compact ? 170 : 'none' }}
          />
        </Tooltip>
      ))}
    </Box>
  );
};

export default SlaBadge;
